import React from 'react';

/**
 * Reusable input field component with consistent styling
 */
export const FormInput = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  required = false,
  helpText = null,
  className = "",
  ...props
}) => {
  return (
    <div className={`space-y-1 ${className}`}>
      <label className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary mb-2">
        {label}
        {required && <span className="text-status-error-light-text dark:text-status-error-dark-text ml-1">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="block w-full px-3 py-2.5 border border-ui-light-border dark:border-ui-dark-border rounded-xl bg-white dark:bg-ui-dark-card text-ui-light-text-primary dark:text-ui-dark-text-primary focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
        {...props}
      />
      {helpText && (
        <p className="text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted mt-1 ml-1">
          {helpText}
        </p>
      )}
    </div>
  );
};

/**
 * Reusable select field component with consistent styling
 */
export const FormSelect = ({
  label,
  value,
  onChange,
  options = [],
  required = false,
  helpText = null,
  className = "",
  ...props
}) => {
  return (
    <div className={`space-y-1 ${className}`}>
      <label className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary mb-2">
        {label}
        {required && <span className="text-status-error-light-text dark:text-status-error-dark-text ml-1">*</span>}
      </label>
      <select
        value={value}
        onChange={onChange}
        required={required}
        className="block w-full px-3 py-2.5 border border-ui-light-border dark:border-ui-dark-border rounded-xl bg-white dark:bg-ui-dark-card text-ui-light-text-primary dark:text-ui-dark-text-primary focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
        {...props}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {helpText && (
        <p className="text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted mt-1 ml-1">
          {helpText}
        </p>
      )}
    </div>
  );
};

/**
 * Reusable file search/select component
 */
export const FormFileSelect = ({
  label,
  searchValue,
  onSearchChange,
  selectedValue,
  onSelectChange,
  files = [],
  required = false,
  helpText = null,
  className = "",
  renderOption = (file) => `${file.filename} ${file.filter ? `[${file.filter}]` : ''} ${file.object_name ? `| ${file.object_name}` : ''}`,
  ...props
}) => {
  return (
    <div className={`space-y-1 ${className}`}>
      <label className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary mb-2">
        {label}
        {required && <span className="text-status-error-light-text dark:text-status-error-dark-text ml-1">*</span>}
      </label>
      {files && files.length > 0 ? (
        <div className="space-y-2">
          <input
            type="text"
            value={searchValue}
            onChange={onSearchChange}
            placeholder="Search for files..."
            className="block w-full px-3 py-2.5 border border-ui-light-border dark:border-ui-dark-border rounded-xl bg-white dark:bg-ui-dark-card text-ui-light-text-primary dark:text-ui-dark-text-primary focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          />
          <select
            value={selectedValue}
            onChange={onSelectChange}
            required={required}
            className="block w-full px-3 py-2.5 border border-ui-light-border dark:border-ui-dark-border rounded-xl bg-white dark:bg-ui-dark-card text-ui-light-text-primary dark:text-ui-dark-text-primary focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            size="5"
            {...props}
          >
            <option value="">Select a file</option>
            {files.map((file) => (
              <option key={file.id} value={file.id}>
                {renderOption(file)}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <input
          type="text"
          value=""
          placeholder="No files available"
          disabled
          className="block w-full px-3 py-2.5 border border-ui-light-border dark:border-ui-dark-border rounded-xl bg-gray-100 text-ui-light-text-secondary"
        />
      )}
      {helpText && (
        <p className="text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted mt-1 ml-1">
          {helpText}
        </p>
      )}
    </div>
  );
};

/**
 * Reusable form group for two fields side by side
 */
export const FormGroup = ({ children, className = "" }) => {
  return (
    <div className={`flex gap-4 ${className}`}>
      {children}
    </div>
  );
};
